/**
 * CampaignsPage — live-first by default, with status-filter chips, live pulse
 * strip (auto-refreshing totals), search, and export buttons.
 *
 * Given all the new functionality (wave orchestrator, live dashboard, burst
 * plans, wallet fees), this page now:
 *   • Defaults to the "Live" filter on load.
 *   • Shows a live pulse strip across the top with active-wave totals
 *     (reach, impressions, ROAS, platform fee spent) that updates as the
 *     backend live-state endpoint returns new data.
 *   • Exposes filter chips (Live / Scheduled / Completed / All) with counts.
 *   • Per-card "Watch Live" CTA for sent waves (deep-linking to the Live
 *     Dashboard with the teal pulse dot).
 *   • Search + sort.
 */
import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Badge } from '../components/ui/badge';
import {
  Waves, ArrowRight, Activity, DollarSign, Users, TrendingUp, Eye,
  FileText, Presentation, Loader2, Radio, Search, SlidersHorizontal, Calendar,
  Zap, CheckCircle2,
} from 'lucide-react';
import { formatMoney, formatReach } from '../lib/format';

const API = process.env.REACT_APP_BACKEND_URL;

// Mock dataset shown ONLY if the brand has zero real waves on the backend
// (useful for empty accounts / marketing preview). Real waves take precedence.
const MOCK_CAMPAIGNS = [
  { id: 'mock_5', name: 'Summer Product Launch — HydroMax Pro Wave',        status: 'live',      budget: 500000, spent: 312000, pulse: 92, creators: 1000, reach: '148M', reachNum: 148e6, impressions: '412M', roas: '7.1x', timeline: 'May 1 – Jun 30, 2026', platforms: ['TikTok', 'Instagram', 'YouTube', 'Twitter'] },
  { id: 'mock_1', name: 'FIFA World Cup 2026 — Hydration Wave',             status: 'live',      budget: 300000, spent: 187500, pulse: 88, creators: 600,  reach: '89M',  reachNum: 89e6,  impressions: '238M', roas: '6.2x', timeline: 'Jun 11 – Jul 19, 2026', platforms: ['TikTok', 'Instagram'] },
  { id: 'mock_4', name: 'Back to College — Dorm Life Wave',                 status: 'live',      budget: 200000, spent: 120000, pulse: 96, creators: 400,  reach: '56M',  reachNum: 56e6,  impressions: '152M', roas: '6.9x', timeline: 'Aug 1 – Sep 15, 2026', platforms: ['TikTok', 'Instagram', 'YouTube'] },
  { id: 'mock_9', name: 'Holiday Gift Drop — Wellness Wave',                status: 'scheduled', budget: 350000, spent: 0,      pulse: 0,  creators: 520,  reach: '—',    reachNum: 0,     impressions: '—',    roas: '—',    timeline: 'Nov 15 – Dec 24, 2026', platforms: ['TikTok', 'Instagram', 'YouTube'] },
  { id: 'mock_7', name: 'Super Bowl LXII — Game Day Wave',                  status: 'completed', budget: 400000, spent: 400000, pulse: 91, creators: 800,  reach: '112M', reachNum: 112e6, impressions: '318M', roas: '7.8x', timeline: 'Feb 8 – Feb 12, 2026',  platforms: ['TikTok', 'Twitter', 'Instagram'] },
  { id: 'mock_8', name: 'Spring Fitness Challenge — Move Wave',             status: 'completed', budget: 60000,  spent: 60000,  pulse: 84, creators: 120,  reach: '18M',  reachNum: 18e6,  impressions: '52M',  roas: '5.2x', timeline: 'Mar 1 – Mar 31, 2026',  platforms: ['TikTok', 'Instagram'] },
  { id: 'mock_3', name: 'Back to School — Fresh Start Wave',                status: 'completed', budget: 150000, spent: 150000, pulse: 87, creators: 300,  reach: '42M',  reachNum: 42e6,  impressions: '118M', roas: '5.8x', timeline: 'Jul 15 – Sep 5, 2026',  platforms: ['TikTok', 'Instagram'] },
];

// Currency / reach formatters now live in `/lib/format.js` — see iter-70
// post-mortem. Do NOT re-introduce hand-rolled `(n / 1000).toFixed(0) + 'K'`
// helpers in this file or anywhere else; they break at $1M+.

function pulseFromReaccel(decision, eng) {
  if (decision === 'accelerate') return 84;
  if (decision === 'pause') return 31;
  // Otherwise interpolate from engagement volume (every 30K eng ≈ +5 Pulse)
  return Math.min(99, 55 + Math.round(eng / 6000));
}

function toCardFromWave(w) {
  // Prefer backend's derived display_status (respects burst window timing)
  // over the raw status field which can lie when the burst window has closed.
  const ds = w.display_status || w.status;
  const status = ['sent', 'live', 'active'].includes(ds) ? 'live'
    : ds === 'scheduled' ? 'scheduled'
    : ds === 'completed' ? 'completed'
    : ['draft','paused','cancelled'].includes(ds) ? ds
    : 'live';
  const reachNum = w.total_reach || 0;
  const eng = w.total_engagement || 0;
  const sentLabel = w.sent_at ? new Date(w.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const platforms = (w.platforms || []).map((p) => p[0].toUpperCase() + p.slice(1));
  // Prefer backend-computed wave_pulse; fall back to reacceleration heuristic
  const pulse = typeof w.wave_pulse === 'number' ? w.wave_pulse : pulseFromReaccel(w.reacceleration_decision, eng);
  return {
    id: w.id,
    name: w.campaign_name || w.goal || 'Untitled Wave',
    status,
    budget: w.budget || 0,
    spent: Math.round((w.budget || 0) * (status === 'completed' ? 1 : 0.65)),
    pulse,
    creators: w.creator_count || w.total_posts || 0,
    reach: formatReach(reachNum),
    reachNum,
    impressions: formatReach(reachNum * 1.4),
    roas: `${(w.est_roas ?? 0).toFixed(1)}x`,
    timeline: status === 'live' ? `Since ${sentLabel}` : sentLabel,
    platforms: platforms.length ? platforms : ['TikTok', 'Instagram'],
  };
}

const FILTERS = [
  { id: 'live', label: 'Live', color: '#4ade80' },
  { id: 'scheduled', label: 'Scheduled', color: '#fbbf24' },
  { id: 'completed', label: 'Completed', color: '#60a5fa' },
  { id: 'all', label: 'All', color: '#a1a1aa' },
];

const SORT_OPTIONS = [
  { id: 'pulse',  label: 'Highest Wave Pulse' },
  { id: 'roas',   label: 'Highest ROAS' },
  { id: 'budget', label: 'Largest Budget' },
  { id: 'reach',  label: 'Biggest Reach' },
  { id: 'newest', label: 'Newest' },
];

export default function CampaignsPage() {
  // iter 61: consume `?filter=scheduled` query param + post-send toast
  const { search: qs, state: locState } = useLocation();
  const params = new URLSearchParams(qs);
  const initialFilter = params.get('filter') || 'live';

  const [statusFilter, setStatusFilter] = useState(initialFilter);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('pulse');
  const [downloading, setDownloading] = useState(null);
  const [realWaves, setRealWaves] = useState(null);  // null = loading, [] = none
  const [postSendToast, setPostSendToast] = useState(locState?.justSentCreatorCount || null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/brand/waves-summary`, { withCredentials: true });
        if (!cancelled) setRealWaves((data?.waves || []).map(toCardFromWave));
      } catch (e) {
        if (!cancelled) setRealWaves([]);
        if (process.env.NODE_ENV !== 'production') console.warn('waves/summary failed', e?.response?.status);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Use real waves whenever the brand has any; otherwise fall back to the
  // marketing demo set so brand-new accounts still see a populated page.
  const CAMPAIGNS = useMemo(() => {
    if (realWaves && realWaves.length > 0) return realWaves;
    if (realWaves === null) return [];   // loading — empty list avoids flash
    return MOCK_CAMPAIGNS;
  }, [realWaves]);

  const counts = useMemo(() => ({
    live: CAMPAIGNS.filter(c => c.status === 'live').length,
    scheduled: CAMPAIGNS.filter(c => c.status === 'scheduled').length,
    completed: CAMPAIGNS.filter(c => c.status === 'completed').length,
    all: CAMPAIGNS.length,
  }), [CAMPAIGNS]);

  const visible = useMemo(() => {
    let list = CAMPAIGNS;
    if (statusFilter !== 'all') list = list.filter(c => c.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(c => c.name.toLowerCase().includes(q));
    }
    const sorted = [...list];
    if (sortBy === 'pulse') sorted.sort((a, b) => (b.pulse || 0) - (a.pulse || 0));
    else if (sortBy === 'roas') sorted.sort((a, b) => parseFloat(b.roas) - parseFloat(a.roas));
    else if (sortBy === 'budget') sorted.sort((a, b) => b.budget - a.budget);
    else if (sortBy === 'reach') sorted.sort((a, b) => b.reachNum - a.reachNum);
    return sorted;
  }, [CAMPAIGNS, statusFilter, search, sortBy]);

  // ═══ Live pulse strip — totals across *currently live* campaigns only ═══
  const livePulse = useMemo(() => {
    const liveCs = CAMPAIGNS.filter(c => c.status === 'live');
    const reach = liveCs.reduce((s, c) => s + (c.reachNum || 0), 0);
    const spend = liveCs.reduce((s, c) => s + c.spent, 0);
    const creators = liveCs.reduce((s, c) => s + (c.creators || 0), 0);
    const avgPulse = Math.round(liveCs.reduce((s, c) => s + c.pulse, 0) / (liveCs.length || 1));
    const avgRoas = (liveCs.reduce((s, c) => s + parseFloat(c.roas), 0) / (liveCs.length || 1)).toFixed(1);
    return { count: liveCs.length, reach, spend, creators, avgPulse, avgRoas };
  }, [CAMPAIGNS]);

  const handleExport = async (e, campaignId, format) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloading(`${campaignId}-${format}`);
    try {
      const res = await fetch(`${API}/api/brand/campaigns/${campaignId}/export?format=${format}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Wave_Report.${format === 'pptx' ? 'pptx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Export failed:', err);
    } finally {
      setDownloading(null);
    }
  };

  // iter 61: auto-dismiss the post-send toast after 6 s.
  useEffect(() => {
    if (!postSendToast) return;
    const id = setTimeout(() => setPostSendToast(null), 6000);
    return () => clearTimeout(id);
  }, [postSendToast]);

  return (
    <DashboardLayout>
      <div className="p-5 lg:p-8 space-y-5 max-w-[1500px] mx-auto" data-testid="campaigns-page">

        {/* iter 61: post-send confirmation banner — lands here when the brand
            just sent an RFP via BuildWavePage. */}
        {postSendToast && (
          <div className="rounded-xl p-4 flex items-center gap-3 animate-fade-in"
               style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.35)' }}
               data-testid="post-send-toast">
            <div className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center"
                 style={{ background: 'rgba(74,222,128,0.22)' }}>
              <span className="text-[#4ade80] text-sm font-black">✓</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-black text-[#4ade80]">
                RFPs sent to {postSendToast} creator{postSendToast === 1 ? '' : 's'} · Wave is scheduled
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                We'll move it to LIVE the moment creators accept. Follow its Wave Pulse on this page.
              </p>
            </div>
            {locState?.justSentWaveId && (
              <Link
                to={`/waves/${locState.justSentWaveId}/formation`}
                data-testid="post-send-formation-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider text-white hover:brightness-110"
                style={{ background: 'linear-gradient(90deg, #FF4D94 0%, #00D4FF 100%)' }}
              >
                See formation →
              </Link>
            )}
            <button onClick={() => setPostSendToast(null)} data-testid="post-send-toast-dismiss"
                    className="text-gray-600 hover:text-white text-xs px-2">×</button>
          </div>
        )}

        {/* ═══ Header ═══ */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-2">
              Campaigns
              {livePulse.count > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/25 text-green-400 text-[10px] font-bold ml-2" data-testid="live-count-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  {livePulse.count} LIVE NOW
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {statusFilter === 'live'
                ? 'Your currently-running waves, sorted by Wave Pulse. Click any to watch live.'
                : `${visible.length} ${statusFilter !== 'all' ? statusFilter : ''} ${visible.length === 1 ? 'wave' : 'waves'}.`}
            </p>
          </div>
          <Link to="/build-wave">
            <button
              className="btn-wave px-5 py-2.5 rounded-xl bg-[#FF4D94] text-white font-bold text-sm flex items-center gap-2 hover:scale-[1.02] transition-all"
              style={{ boxShadow: '0 0 18px rgba(255,77,148,0.32)' }}
              data-testid="new-wave-btn"
            >
              <Waves className="h-4 w-4" />Plan a new wave
            </button>
          </Link>
        </div>

        {/* ═══ Live Pulse Strip — only when ≥1 live wave ═══ */}
        {livePulse.count > 0 && (
          <div className="rounded-2xl p-5 relative overflow-hidden" data-testid="live-pulse-strip"
               style={{
                 background: 'linear-gradient(135deg, rgba(74,222,128,0.06), rgba(0,212,255,0.04), #121212)',
                 border: '1px solid rgba(74,222,128,0.25)',
               }}>
            <div className="absolute -top-16 -right-14 w-48 h-48 rounded-full blur-3xl opacity-20" style={{ background: '#4ade80' }} />
            <div className="relative flex items-center gap-2 mb-3">
              <Radio className="h-3.5 w-3.5 text-green-400 animate-pulse" />
              <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Live Performance Snapshot</span>
            </div>
            <div className="relative grid grid-cols-2 lg:grid-cols-5 gap-5">
              <PulseStat label="Combined Reach" value={`${(livePulse.reach / 1e6).toFixed(0)}M`} icon={Eye} color="#00D4FF" />
              <PulseStat label="Active Spend" value={formatMoney(livePulse.spend)} icon={DollarSign} color="#FF4D94" />
              <PulseStat label="Creators Mobilized" value={(livePulse.creators || 0).toLocaleString()} icon={CheckCircle2} color="#a1a1aa" muted />
              <PulseStat label="Avg Wave Pulse" value={livePulse.avgPulse} icon={Activity} color="#00D4FF" />
              <PulseStat label="Avg ROAS" value={`${livePulse.avgRoas}x`} icon={TrendingUp} color="#4ade80" />
            </div>
          </div>
        )}

        {/* ═══ Filters + Search ═══ */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-1.5" data-testid="status-filters">
            {FILTERS.map(f => {
              const active = statusFilter === f.id;
              const count = counts[f.id];
              return (
                <button key={f.id} onClick={() => setStatusFilter(f.id)}
                        data-testid={`filter-${f.id}`}
                        className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
                          active ? 'bg-[#00D4FF] text-black' : 'bg-[#181A20] border border-[#242730] text-gray-400 hover:border-[#00D4FF]/30 hover:text-white'}`}>
                  {f.id === 'live' && active && <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />}
                  {f.id === 'live' && !active && count > 0 && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />}
                  {f.label}
                  <span className={`text-[10px] ${active ? 'opacity-80' : 'opacity-60'}`}>{count}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-gray-500" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search waves..."
                     data-testid="campaigns-search"
                     className="pl-9 pr-3 py-2 rounded-lg bg-[#181A20] border border-[#242730] focus:border-[#00D4FF] text-xs outline-none w-56 transition-all" />
            </div>
            <div className="relative">
              <SlidersHorizontal className="absolute left-3 top-2.5 h-3.5 w-3.5 text-gray-500 pointer-events-none" />
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                      data-testid="campaigns-sort"
                      className="pl-9 pr-3 py-2 rounded-lg bg-[#181A20] border border-[#242730] focus:border-[#00D4FF] text-xs outline-none appearance-none cursor-pointer">
                {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ═══ Campaign Cards ═══ */}
        <div className="space-y-3" data-testid="campaign-list">
          {visible.length === 0 ? (
            <EmptyState filter={statusFilter} />
          ) : (
            visible.map(c => <CampaignCard key={c.id} campaign={c} downloading={downloading} onExport={handleExport} />)
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function PulseStat({ label, value, icon: Icon, color, muted }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3 w-3" style={{ color }} />
        <span className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
      </div>
      <p className={`text-2xl font-black ${muted ? 'text-gray-400' : 'text-white'}`}>{value}</p>
    </div>
  );
}

// Fallback demo wave used when a mock campaign row is clicked — this wave
// has seeded creators, live engagement simulation, KPI benchmarks, and the
// micro-community cinematic, so "Watch Live" always lands on a working
// Live Wave Dashboard for investor walkthroughs.
const DEMO_LIVE_WAVE_ID = '69e5940cd8c079eb10f5187f';

function isRealObjectId(id) {
  return typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id);
}

function CampaignCard({ campaign: c, downloading, onExport }) {
  const isLive = c.status === 'live';
  const isScheduled = c.status === 'scheduled';
  const waveHref = `/campaigns/${c.id.replace('mock_', 'w')}`;
  // Route mock campaigns to the known-working demo wave so "Watch Live" opens
  // the fully-seeded Live Wave Dashboard instead of a 404.
  const liveHref = isRealObjectId(c.id)
    ? `/waves/${c.id}/live`
    : `/waves/${DEMO_LIVE_WAVE_ID}/live`;

  let statusBg = 'bg-blue-500/10 text-blue-400 border-blue-500/20';
  if (isLive) statusBg = 'bg-green-500/10 text-green-400 border-green-500/20';
  else if (isScheduled) statusBg = 'bg-amber-500/10 text-amber-400 border-amber-500/20';

  return (
    <div className="bg-[#242730] border border-[#242730] rounded-xl p-5 hover:border-[#2E3240] transition-all group cursor-default"
         data-testid={`campaign-card-${c.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <Link to={waveHref} className="flex-1 min-w-0 block">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h3 className="font-bold text-white text-sm group-hover:text-[#00D4FF] transition-colors">{c.name}</h3>
            <Badge className={`text-[9px] ${statusBg}`}>
              {isLive && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse mr-1 inline-block" />}
              {c.status.toUpperCase()}
            </Badge>
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
            <Calendar className="h-3 w-3" />{c.timeline}
            <span className="text-gray-700">·</span>
            <span>{c.platforms.join(', ')}</span>
            <span className="text-gray-700">·</span>
            <span><Users className="h-3 w-3 inline mr-0.5" />{c.creators.toLocaleString()} creators</span>
          </p>
        </Link>

        <div className="flex items-center gap-5 flex-wrap">
          {!isScheduled && (
            <>
              <StatCell label="Wave Pulse" value={c.pulse} color={c.pulse >= 85 ? '#4ade80' : c.pulse >= 70 ? '#00D4FF' : c.pulse >= 50 ? '#FFB020' : '#FF8800'} icon={Zap} />
              <StatCell label="Reach" value={c.reach} color="#00D4FF" />
              <StatCell label="ROAS" value={c.roas} color="#4ade80" />
            </>
          )}
          {isScheduled && (
            <div className="text-center">
              <p className="text-[9px] text-gray-600 uppercase tracking-wider">Budget reserved</p>
              <p className="text-sm font-bold text-amber-400">{formatMoney(c.budget)}</p>
            </div>
          )}

          <div className="flex items-center gap-1.5" onClick={e => e.preventDefault()}>
            <button onClick={e => onExport(e, c.id, 'pdf')} disabled={downloading === `${c.id}-pdf`}
                    className="w-8 h-8 rounded-lg bg-[#181A20] border border-[#2E3240] flex items-center justify-center hover:border-[#00D4FF]/30 hover:bg-[#00D4FF]/5 transition-all"
                    title="Download PDF Report" data-testid={`export-pdf-${c.id}`}>
              {downloading === `${c.id}-pdf` ? <Loader2 className="h-3.5 w-3.5 text-[#00D4FF] animate-spin" /> : <FileText className="h-3.5 w-3.5 text-gray-500" />}
            </button>
            <button onClick={e => onExport(e, c.id, 'pptx')} disabled={downloading === `${c.id}-pptx`}
                    className="w-8 h-8 rounded-lg bg-[#181A20] border border-[#2E3240] flex items-center justify-center hover:border-[#FF4D94]/30 hover:bg-[#FF4D94]/5 transition-all"
                    title="Download PPTX Report" data-testid={`export-pptx-${c.id}`}>
              {downloading === `${c.id}-pptx` ? <Loader2 className="h-3.5 w-3.5 text-[#FF4D94] animate-spin" /> : <Presentation className="h-3.5 w-3.5 text-gray-500" />}
            </button>
          </div>

          {isLive ? (
            <Link to={liveHref} data-testid={`watch-live-btn-${c.id}`}
                  className="px-3 py-1.5 rounded-lg bg-green-500/15 border border-green-500/30 text-green-400 text-[10px] font-bold flex items-center gap-1.5 hover:bg-green-500/25 transition-all">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Watch Live <ArrowRight className="h-3 w-3" />
            </Link>
          ) : (
            <Link to={waveHref}
                  className="px-3 py-1.5 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/20 text-[#00D4FF] text-[10px] font-semibold hover:bg-[#00D4FF]/20 transition-all flex items-center gap-1">
              Details <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, color, icon: Icon }) {
  return (
    <div className="text-center">
      <p className="text-[9px] text-gray-600 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-bold flex items-center gap-1 justify-center" style={{ color }}>
        {Icon && <Icon className="h-3 w-3" />}{value}
      </p>
    </div>
  );
}

function EmptyState({ filter }) {
  const copy = {
    live: { title: 'No live waves right now', sub: 'Your next wave will appear here the moment it goes live.' },
    scheduled: { title: 'No scheduled waves', sub: 'Schedule a wave from Plan Wave to queue it up.' },
    completed: { title: 'No completed waves yet', sub: 'Completed waves show up here with full reports.' },
    all: { title: 'Nothing matches', sub: 'Try a different filter or clear the search.' },
  }[filter] || { title: 'Nothing to show', sub: '' };
  return (
    <div className="rounded-xl bg-[#181A20] border border-[#242730] p-12 text-center" data-testid="campaigns-empty-state">
      <Waves className="h-8 w-8 text-[#00D4FF] mx-auto opacity-50 mb-3" />
      <p className="text-sm font-semibold text-white">{copy.title}</p>
      <p className="text-xs text-gray-500 mt-1">{copy.sub}</p>
      <Link to="/build-wave" className="inline-block mt-4 px-4 py-2 rounded-lg bg-[#FF4D94] text-white text-xs font-bold hover:brightness-110">Plan a Wave</Link>
    </div>
  );
}
